
import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCurrencySettings } from '@/hooks/useCurrencySettings';
import { ReportPeriod } from '@/types/taxReport';

interface TaxReportDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  reportPeriod: ReportPeriod;
  onReportPeriodChange: (period: ReportPeriod) => void;
  onGenerate: () => void;
  isGenerating: boolean;
}

const TaxReportDialog: React.FC<TaxReportDialogProps> = ({
  isOpen,
  onOpenChange,
  reportPeriod,
  onReportPeriodChange,
  onGenerate,
  isGenerating
}) => {
  const { t } = useTranslation('payments');
  const { defaultCurrency } = useCurrencySettings();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("payments:taxReport.generate")}</DialogTitle>
          <DialogDescription>
            {t("payments:taxReport.description")} {defaultCurrency}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="tax-report-year">{t("payments:taxReport.year", { defaultValue: 'Report year' })}</Label>
            <Input
              id="tax-report-year"
              type="number"
              min={1900}
              max={9999}
              step={1}
              value={reportPeriod.startDate.split('-')[0]}
              onChange={(e) => {
                const year = e.target.value;
                onReportPeriodChange({ startDate: `${year}-01-01`, endDate: `${year}-12-31` });
              }}
            />
            <p className="text-sm text-muted-foreground">
              {t("payments:taxReport.allStudents", { defaultValue: 'Includes all students in your dojo, regardless of the payment history filters.' })}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("payments:taxReport.cancel")}
          </Button>
          <Button onClick={onGenerate} disabled={isGenerating || !/^\d{4}-01-01$/.test(reportPeriod.startDate) || Number(reportPeriod.startDate.slice(0, 4)) < 1900}>
            {isGenerating ? (
              <>{t("payments:taxReport.generating")}</>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                {t("payments:taxReport.downloadTitle")}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TaxReportDialog;
